import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Users, Calendar, Clock, Globe, MapPin, Handshake, Info } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import type { MeetupPostData } from '../../types';

interface MeetupPostContentProps {
  post: MeetupPostData;
}

export function MeetupPostContent({ post: meetup }: MeetupPostContentProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          <Handshake className="h-5 w-5" />
        </div>
        <span className="font-bold text-foreground">Meetup</span>
      </div>

      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl border border-primary/10 overflow-hidden">
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <Badge
              variant="outline"
              className="bg-background/50 backdrop-blur-sm border-primary/20 text-primary capitalize"
            >
              {meetup.meetupType?.replaceAll('_', ' ').toLowerCase() || 'Meetup'}
            </Badge>
            <div className="flex items-center text-xs font-semibold text-muted-foreground">
              {meetup.meetupParticipants && meetup.meetupParticipants.length > 0 ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer">
                      <div className="flex -space-x-2 mr-2">
                        {meetup.meetupParticipants.slice(0, 3).map((p) => (
                          <Avatar
                            key={p.id}
                            className="w-6 h-6 border-2 border-background pointer-events-none"
                          >
                            {p.profileImage && <AvatarImage src={p.profileImage} />}
                            <AvatarFallback className="text-[10px]">
                              {p.firstName[0]}
                              {p.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {meetup.meetupParticipants.length > 3 && (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium border-2 border-background z-10 pointer-events-none">
                            +{meetup.meetupParticipants.length - 3}
                          </div>
                        )}
                      </div>
                      {meetup.participantCount} Participants
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-64 p-3 bg-background border border-border shadow-md rounded-xl"
                    align="end"
                  >
                    <div className="flex flex-col space-y-2">
                      <h4 className="font-semibold text-sm mb-1 text-foreground">
                        Meetup Participants
                      </h4>
                      <div className="max-h-32 overflow-y-auto space-y-2 pr-1">
                        {meetup.meetupParticipants.map((p) => (
                          <div key={p.id} className="flex items-center gap-3">
                            <Avatar className="w-8 h-8 shrink-0">
                              {p.profileImage && <AvatarImage src={p.profileImage} />}
                              <AvatarFallback className="text-xs">
                                {p.firstName[0]}
                                {p.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-foreground leading-tight truncate">
                                {p.firstName} {p.lastName}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <>
                  <Users className="w-3.5 h-3.5 mr-1" />
                  {meetup.participantCount} Participants
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center text-sm font-medium text-foreground gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              {new Date(meetup.meetupDate).toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
            <div className="flex items-center text-sm text-muted-foreground gap-2">
              <Clock className="w-4 h-4 text-muted-foreground/60" />
              {meetup.meetupTime || 'Time TBD'}
              {meetup.meetupDuration && ` (${meetup.meetupDuration})`}
            </div>
            <div className="flex items-center text-sm text-muted-foreground gap-2">
              {meetup.meetupMode === 'ONLINE' ? (
                <>
                  <Globe className="w-4 h-4 text-muted-foreground/60" />
                  <span className="truncate">Online</span>
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 text-muted-foreground/60" />
                  <span className="truncate">
                    {meetup.meetupLocation === 'Other'
                      ? meetup.meetupOtherLocation
                      : meetup.meetupLocation}
                  </span>
                </>
              )}
            </div>
            {meetup.powerVisitDetails && (
              <div className="flex items-center text-sm text-muted-foreground gap-2">
                <Info className="w-4 h-4 text-muted-foreground/60"></Info>
                <span className="text-sm text-muted-foreground gap-2">
                  {meetup.powerVisitDetails}
                </span>
              </div>
            )}
          </div>
        </div>

        {meetup.meetupAttachments && meetup.meetupAttachments.length > 0 && (
          <div
            className={`grid gap-px border-t border-primary/10 grid-cols-${meetup.meetupAttachments.length > 1 ? 2 : 1}`}
          >
            {meetup.meetupAttachments.slice(0, 2).map((img: string, idx: number) => (
              <Dialog key={idx}>
                <DialogTrigger asChild>
                  <img
                    src={img}
                    alt="Meetup overlay"
                    className="w-full h-32 object-contain cursor-pointer transition-opacity hover:opacity-80"
                  />
                </DialogTrigger>
                <DialogContent className="max-w-5xl w-[90vw] p-0 overflow-hidden bg-black/65 shadow-2xl border-0 flex items-center justify-center [&>button]:text-white [&>button]:bg-black/40 hover:[&>button]:bg-black/60 [&>button]:p-2 [&>button]:rounded-full">
                  <img
                    src={img}
                    alt="Meetup zoomed"
                    className="w-full max-h-[85vh] object-contain"
                  />
                </DialogContent>
              </Dialog>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
